import { PlusAddIcon } from '@/components/svg/PlusAddIcon'
import { TipsySwitch } from '@/components/ui-kit/TipsySwitch'
import { MAX_USER_UPLOAD_ANIMATED_SIZE } from '@/constant/img'
import { useUploadGIF } from '@/hooks/swr/useUploadGIF'
import { CreateDeleteIcon } from '@/icons'
import { createStateFactory } from '@/store/create'
import { useMemoizedFn } from 'ahooks'
import { message } from 'antd'
import { usePathname, useSearchParams } from 'next/navigation'
import {
  ChangeEvent,
  ClipboardEvent,
  DragEvent,
  useCallback,
  useRef,
} from 'react'
import { useRecoilState } from 'recoil'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import LoadingImg from '@/icons/LoadingImg'
import useI18n from 'Hooks/useI18n'
import { withLocale } from '@/locales'
import { isVideoFile, isVideoUrl } from 'Utils/img'

type Props = {
  disabled: boolean
  scene?: string
}

export default function UploadGif({
  disabled,
  scene = 'character_image',
}: Props) {
  const { t, lang } = useI18n()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const isUpdateChar =
    pathname === withLocale('/update', lang) ||
    searchParams.get('update') === 'true'
  const [createCharForm, setCreateCharForm] = useRecoilState(
    createStateFactory(false, scene)
  )
  const [updateCharForm, setUpdateCharForm] = useRecoilState(
    createStateFactory(true, scene)
  )
  const charForm = isUpdateChar ? updateCharForm : createCharForm
  const setCharForm = isUpdateChar ? setUpdateCharForm : setCreateCharForm

  const {
    showUploadGif,
    uploadGifReference,
    uploadGifReferencePreviewUrl,
    uploadGifUploading,
  } = charForm

  const { triggerUploadGIF, isMutating: isUploadingGif } = useUploadGIF()

  const setShowUploadGif = (value: boolean) => {
    setCharForm((prev) => ({ ...prev, showUploadGif: value }))
  }
  const setGifReference = (file: File | null) => {
    setCharForm((prev) => ({ ...prev, uploadGifReference: file }))
  }
  const setGifReferencePreviewUrl = (url: string | null) => {
    setCharForm((prev) => ({
      ...prev,
      uploadGifReferencePreviewUrl: url,
    }))
  }
  const setGifReferencePath = useCallback(
    (path: string | null) => {
      setCharForm((prev) => ({
        ...prev,
        uploadGifReferencePath: path,
      }))
    },
    [setCharForm]
  )

  const setUploadGifKey = useCallback(
    (value: string | undefined) => {
      setCharForm((prev) => ({
        ...prev,
        uploadGifKey: value,
      }))
    },
    [setCharForm]
  )
  const setUploadGifUrl = useCallback(
    (value: string | undefined) => {
      setCharForm((prev) => ({
        ...prev,
        uploadGifUrl: value,
      }))
    },
    [setCharForm]
  )
  const setUploadGifUploading = useCallback(
    (value: boolean) => {
      setCharForm((prev) => ({
        ...prev,
        uploadGifUploading: value,
      }))
    },
    [setCharForm]
  )
  // 状态

  // 处理图片上传部分
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleSwitchAdvance = (checked: boolean) => {
    setGifReference(null)
    setGifReferencePreviewUrl(null)
    setGifReferencePath(null)
    setUploadGifKey(undefined)
    setUploadGifUrl(undefined)

    setShowUploadGif(checked)
  }

  const handleUploadCharGif = useMemoizedFn(
    async (uploadGifReference: File | null) => {
      if (!uploadGifReference) {
        message.error({
          content: t('Please upload a GIF, WebP or MP4 file'),
          icon: <ExclamationCircleOutlined />,
        })
        return
      }

      try {
        setUploadGifUploading(true)
        setUploadGifKey(undefined)
        setUploadGifUrl(undefined)

        const uploadCharGifRes = await triggerUploadGIF(uploadGifReference)
        if (!uploadCharGifRes?.key) {
          throw new Error('Failed to upload file')
        }
        setUploadGifKey(uploadCharGifRes.key)
        setUploadGifUrl(uploadCharGifRes.url)
      } catch (error) {
        console.error('Upload task error:', error)
        setGifReference(null)
        setGifReferencePreviewUrl(null)
        setGifReferencePath(null)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        message.error({
          content: t('Upload failed, please try again'),
          icon: <ExclamationCircleOutlined />,
        })
      } finally {
        setUploadGifUploading(false)
      }
    }
  )

  const handleSaveGif = async (userFile: File) => {
    if (userFile.size > MAX_USER_UPLOAD_ANIMATED_SIZE) {
      message.error(
        t('The uploaded file exceeds the maximum allowed size of 20MB')
      )
      return
    }
    setGifReference(userFile)
    setGifReferencePreviewUrl(URL.createObjectURL(userFile))
    setGifReferencePath(null)

    handleUploadCharGif(userFile)
  }

  const handleImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files.length) return
    const type = e.target.files[0].type
    const isValidType =
      type.indexOf('gif') >= 0 ||
      type.indexOf('webp') >= 0 ||
      type === 'video/mp4'
    if (!isValidType) {
      message.error(t('Please upload a GIF, WebP or MP4 file'))
      return
    }
    const userFile: File = e.target.files[0]
    handleSaveGif(userFile)
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleSaveGif(e.dataTransfer.files[0])
    }
  }

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    if (e.clipboardData.files.length) {
      handleSaveGif(e.clipboardData.files[0])
    }
  }

  const handleClick = () => {
    if (uploadGifReference) return
    fileInputRef.current?.click()
  }

  const handleCancel = () => {
    setGifReference(null)
    setGifReferencePreviewUrl(null)
    setGifReferencePath(null)
    setUploadGifKey(undefined)
    setUploadGifUrl(undefined)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const renderUploadGif = () => {
    return (
      <div className=" relative flex h-full flex-1 flex-col justify-center rounded-lg bg-white/5">
        {isUploadingGif && (
          <div className="absolute inset-0 z-10 flex cursor-not-allowed items-center justify-center rounded-lg bg-black/20">
            <LoadingImg />
          </div>
        )}
        {!showUploadGif && (
          <div className="absolute inset-0 z-10 flex cursor-not-allowed items-center justify-center rounded-lg bg-black/10 backdrop-blur-[2px] backdrop-filter"></div>
        )}
        <div className="flex h-full items-center justify-center p-4">
          <div
            onDragOver={!uploadGifReference ? handleDragOver : undefined}
            onDrop={!uploadGifReference ? handleDrop : undefined}
            onPaste={!uploadGifReference ? handlePaste : undefined}
            onClick={!uploadGifReference ? handleClick : undefined}
            className="relative flex h-full w-full cursor-pointer flex-col items-center justify-center rounded-lg border border-white/20 bg-[rgba(0,0,0,0.02)]"
          >
            {uploadGifReference ? (
              <>
                <div className="flex h-[90%] w-[70%] flex-col items-center justify-center">
                  {isVideoFile(uploadGifReference) ? (
                    <video
                      src={uploadGifReferencePreviewUrl || ''}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="aspect-[9/16] h-full rounded-lg object-cover"
                    />
                  ) : (
                    <img
                      src={uploadGifReferencePreviewUrl || ''}
                      alt={
                        /* i18n-ignore */ 'Animated preview of the selected reference'
                      }
                      className="aspect-[9/16] h-full rounded-lg object-cover"
                    />
                  )}
                  <div className="absolute right-[5px] top-[5px] cursor-pointer">
                    {!disabled && <CreateDeleteIcon onClick={handleCancel} />}
                  </div>
                </div>
              </>
            ) : (
              <>
                <input
                  type="file"
                  accept="image/webp, image/gif, video/mp4"
                  name="file"
                  ref={fileInputRef}
                  onChange={handleImageChange}
                  className="hidden"
                  disabled={disabled}
                />
                <PlusAddIcon size={36} />
                <div className="px-5 pt-5 text-center text-sm text-[#756A6E]">
                  {t('For best results, use a high-resolution GIF or MP4')}
                  {t("(9:16) to make your character's home page photo dynamic")}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full max-w-[360px] flex-col">
      <div className="mx-[9px] mb-[10px] flex h-5 items-center justify-between">
        <h4 className="text-sm leading-4 text-white">
          {t('Upload Dynamic Character')}
        </h4>
        <TipsySwitch checked={showUploadGif} onChange={handleSwitchAdvance} />
      </div>
      {renderUploadGif()}
    </div>
  )
}
